import { useState } from 'react';
import { Clock, XCircle, CheckCircle2, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { usePaymentSettings, useLatestPayment } from '@/hooks/usePayments';
import type { WeddingInvitation } from '@/types/invitation';
import PaymentModal from '@/components/payments/PaymentModal';

interface Props {
  invitation: WeddingInvitation;
}

const formatBs = (n: number) =>
  new Intl.NumberFormat('es-VE', { maximumFractionDigits: 0 }).format(n);

const PublishPaymentBanner = ({ invitation }: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data: settings } = usePaymentSettings();
  const { data: latestPayment } = useLatestPayment(invitation.id);

  // Already paid: show a discreet confirmation instead of the CTA.
  if (invitation.is_paid) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="flex items-center gap-3 p-4">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" />
          <p className="text-sm font-medium text-green-800">
            Invitación publicada. ¡El pago fue confirmado!
          </p>
        </CardContent>
      </Card>
    );
  }

  const priceUsd = settings?.publishPriceUsd ?? 0;
  const priceBs = priceUsd * (settings?.usdToBsRate ?? 0);
  const status = latestPayment?.status;

  // Payment under review.
  if (status === 'pending') {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="flex items-center gap-3 p-4">
          <Clock className="h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900">Pago en revisión</p>
            <p className="text-sm text-amber-800">
              Estamos verificando tu comprobante. Te avisaremos al aprobarlo.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Payment rejected: explain why and let them retry.
  if (status === 'rejected') {
    return (
      <>
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <div>
                <p className="text-sm font-medium text-destructive">Pago rechazado</p>
                <p className="text-sm text-muted-foreground">
                  {latestPayment?.rejection_reason || 'Revisa los datos y vuelve a intentarlo.'}
                </p>
              </div>
            </div>
            <Button size="sm" className="shrink-0" onClick={() => setModalOpen(true)}>
              Reintentar
            </Button>
          </CardContent>
        </Card>
        <PaymentModal open={modalOpen} onOpenChange={setModalOpen} invitationId={invitation.id} />
      </>
    );
  }

  // Default: draft, no active payment → invite to pay.
  return (
    <>
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Rocket className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">Tu invitación está en modo borrador</p>
              <p className="text-sm text-muted-foreground">
                Publícala pagando USD {priceUsd.toFixed(2)}
                {priceBs > 0 && <> (≈ {formatBs(priceBs)} Bs)</>}.
              </p>
            </div>
          </div>
          <Button size="sm" className="shrink-0" onClick={() => setModalOpen(true)}>
            Pagar y publicar
          </Button>
        </CardContent>
      </Card>
      <PaymentModal open={modalOpen} onOpenChange={setModalOpen} invitationId={invitation.id} />
    </>
  );
};

export default PublishPaymentBanner;
